import { path, pathOr } from 'ramda';
import { createSelector } from 'reselect';
import { SelectedCustomerSelector } from './customer.selectors';
import { CoreLocaleKeys, i18n } from 'invision-core';
import { CurrentRedemptionDetailsSelector } from './coupon.redemption.code.selectors';
import moment from 'moment';
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.customerCouponsLists;
};
export var CustomerCouponsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return pathOr(null, ['coupons'], selectedCustomer);
});
export var CustomerCouponsErrorSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(null, ['couponsError'], customerCoupons);
});
export var IsFetchingRedemptionCodesSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(false, ['isFetchingRedemptionCodes'], customerCoupons);
});
export var IsFetchingCouponRedemptionsSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(false, ['isFetchingCouponRedemptions'], customerCoupons);
});
export var IsFetchingGrantableCouponsSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(false, ['isFetchingGrantableCoupons'], customerCoupons);
});
export var IsGrantingCouponSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(false, ['isGrantingCoupon'], customerCoupons);
});
export var LastGrantedCouponDetailsSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(null, ['data', 'lastGrantedCouponDetails'], customerCoupons);
});
export var CustomerCouponRedemptionsSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr([], ['data', 'couponRedemptions'], customerCoupons);
});
export var CustomerRedemptionCodesSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr([], ['data', 'redemptionCodes'], customerCoupons);
});
export var GrantableCouponsSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr([], ['data', 'grantableCoupons'], customerCoupons);
});
export var SelectedCouponSelector = createSelector([CustomerCouponsSelector], function (customerCoupons) {
  return pathOr(null, ['data', 'selectedCoupon'], customerCoupons);
});
export var CurrentRedemptionDetailsAssociationCardsDataSelector = createSelector([CurrentRedemptionDetailsSelector], function (currentRedemptionDetails) {
  if (path(['Coupon', 'Associations'], currentRedemptionDetails)) {
    return currentRedemptionDetails.Coupon.Associations.map(function (association) {
      return {
        ProductId: association.ProductId,
        ProductName: association.ProductName,
        ProductCategoryId: association.ProductCategoryId,
        ProductCategoryName: association.ProductCategoryName,
        PricingPlanId: association.PricingPlanId,
        PricingPlanName: association.PricingPlanName
      };
    });
  } else {
    return [];
  }
});
export var SelectedCouponAssociationCardsDataSelector = createSelector([SelectedCouponSelector], function (selectedCoupon) {
  if (selectedCoupon && selectedCoupon.CouponAssociations) {
    return selectedCoupon.CouponAssociations.map(function (association) {
      return {
        ProductId: association.ProductId,
        ProductName: association.ProductName,
        ProductCategoryId: association.ProductCategoryId,
        ProductCategoryName: association.ProductCategoryName,
        PricingPlanId: association.PricingPlanId,
        PricingPlanName: association.PricingPlanName
      };
    });
  } else {
    return [];
  }
});
export var GrantCouponsTableDataSelector = createSelector([GrantableCouponsSelector], function (grantableCoupons) {
  var tableData = [];
  if (grantableCoupons && grantableCoupons.Coupons) {
    grantableCoupons.Coupons.forEach(function (coupon) {
      tableData.push({
        CouponData: coupon,
        DateAdded: coupon.Added || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE),
        Name: coupon.Name || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE),
        Discount: coupon.DiscountName || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE)
      });
    });
  }
  return tableData;
});
export var GrantCouponsCurrentPageNumberSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(0, ['grantCouponsCurrentPageNumber'], uiState);
});
export var GrantCouponsTotalPageNumberSelector = createSelector([GrantableCouponsSelector], function (grantableCoupons) {
  return pathOr(0, ['PageCount'], grantableCoupons);
});
export var GrantCouponsRecordCountSelector = createSelector([GrantableCouponsSelector], function (grantableCoupons) {
  return pathOr(0, ['RecordCount'], grantableCoupons);
});
export var GrantCouponsNameFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(undefined, ['grantCouponsNameFilter'], uiState);
});
export var GrantCouponsShouldShowNoResultsSelector = createSelector([IsFetchingGrantableCouponsSelector, GrantCouponsRecordCountSelector], function (fetchingCoupons, recordCount) {
  return !fetchingCoupons && recordCount < 1;
});
export var GrantCouponsShouldShowEmptyStateSelector = createSelector([GrantCouponsShouldShowNoResultsSelector, GrantCouponsNameFilterSelector], function (shouldShowNoResults, nameFilter) {
  return shouldShowNoResults && !nameFilter;
});
export var GrantedCouponsRecordCountSelector = createSelector([CustomerRedemptionCodesSelector], function (redemptionCodes) {
  return pathOr(0, ['RecordCount'], redemptionCodes);
});
export var GrantedCouponsCurrentPageNumberSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(1, ['grantedCouponsCurrentPageNumber'], uiState);
});
export var GrantedCouponsGrantDateEndFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(undefined, ['grantedCouponsGrantDateEndFilter'], uiState);
});
export var GrantedCouponsGrantStartFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(undefined, ['grantedCouponsGrantDateStartFilter'], uiState);
});
export var GrantedCouponsNameOrCodeFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(undefined, ['grantedCouponsNameOrCodeFilter'], uiState);
});
export var GrantedCouponsRedemptionStatusFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(undefined, ['grantedCouponsRedemptionStatusFilter'], uiState);
});
export var GrantCouponsSelectedPageSizePreference = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(null, ['grantedCouponsPageSizePreference'], uiState);
});
export var SelectedPageSizePreference = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(null, ['pageSizePreference'], uiState);
});

//This selector combines both the redeemed redemption codes with codes that haven't been redeemed yet
export var GrantedCouponsTableDataSelector = createSelector([CustomerCouponRedemptionsSelector, CustomerRedemptionCodesSelector], function (couponRedemptions, redemptionCodes) {
  var tableData = [];
  if (redemptionCodes && redemptionCodes.RedemptionCodes) {
    redemptionCodes.RedemptionCodes.forEach(function (redemptionCodeData) {
      var latestRedemption = {};
      var orderId = undefined;
      var orderNumber = i18n.translate(CoreLocaleKeys.NOT_APPLICABLE);
      var lastRedeemed = i18n.translate(CoreLocaleKeys.NOT_APPLICABLE);
      if (redemptionCodeData.RedemptionsCount && couponRedemptions && couponRedemptions.CouponRedemptions) {
        var redemptionInstances = couponRedemptions.CouponRedemptions.filter(function (redemption) {
          return redemption.RedemptionCode === redemptionCodeData.RedemptionCode;
        }).asMutable();
        if (redemptionInstances.length > 0) {
          var sortedRedemptionInstances = redemptionInstances.sort(function (redemptionA, redemptionB) {
            if (moment(redemptionA.RedemptionDate).isBefore(redemptionB.RedemptionDate)) {
              return 1;
            } else if (moment(redemptionA.RedemptionDate).isAfter(redemptionB.RedemptionDate)) {
              return -1;
            } else {
              return 0;
            }
          });
          latestRedemption = sortedRedemptionInstances[0];
          lastRedeemed = latestRedemption.RedemptionDate;
          orderId = latestRedemption.OrderId;
          orderNumber = latestRedemption.OrderNumber;
        }
      }
      var maxRedemptions = pathOr(undefined, ['Coupon', 'SubscriberMaxRedemptions'], latestRedemption);
      tableData.push({
        DateGranted: redemptionCodeData.Granted || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE),
        Redemptions: redemptionCodeData.RedemptionsCount || undefined,
        MaxRedemptions: maxRedemptions,
        RedemptionCode: redemptionCodeData.RedemptionCode || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE),
        Coupon: redemptionCodeData.CouponName || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE),
        RedemptionCodeId: redemptionCodeData.Id,
        OrderId: orderId,
        OrderNumber: orderNumber,
        LastRedeemed: lastRedeemed,
        ExpirationDate: redemptionCodeData.Expiration || i18n.translate(CoreLocaleKeys.NOT_APPLICABLE)
      });
    });
  }
  return tableData;
});